import { Color, Spacing } from '@persona-id/design-tokens';
import { MouseEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import { paragraphMixins } from 'design-system/mixins/text';
import { Elevation } from 'design-system/variables/effect';

interface RenderArgs {
  setPickerActive: (val: boolean) => void;
  pickerDown: () => void;
  pickerUp: () => void;
}

export interface Option {
  label: string;
  value: string;
}

interface PickerProps {
  options: Option[];
  onPickerClick: (option: Option, e: MouseEvent<HTMLLIElement>) => void;
  // Note: option may be null, if there's no viable option (eg. when nothing passes the search filter).
  onValueChange: (option: Option | null) => void;
  render: (args: RenderArgs) => ReactElement;
  searchTerm: string;
}

const PickerContainer = styled.div`
  position: absolute;
  background: white;
  padding: ${Spacing[2]} ${Spacing[1]};
  border-radius: ${BORDER_RADIUSES.rounded};
  box-shadow: ${Elevation.Mid};
  max-height: 300px;
  overflow-y: scroll;
  min-width: 240px;
  border: 1px solid ${Color.CoreSlate100};
`;

const PickerPositioner = styled.div`
  position: relative;
  z-index: 1;
  color: black;
`;

const PickerMenu = styled.ul`
  list-style-type: none;
  margin-bottom: 0;
  padding: ${Spacing[1]};
`;

const PickerItem = styled.li<{ selected: boolean; hide: boolean }>`
  ${paragraphMixins.md}
  padding: ${Spacing[2]};
  background: ${({ selected }) => (selected ? Color.CorePurple100 : 'none')};
  border-radius: ${BORDER_RADIUSES.rounded};
  min-width: 128px;
  ${({ hide }) => (hide ? `display: none` : null)}
`;

const NoOptionsText = styled.p`
  color: ${Color.CoreSlate700};
`;

// Checks whether a search option matches the search string.
//
// Note: It currently returns whether the search string can be found as a sequential subset
// characters from the label string.
const searchMatch = (label: string, search: string) => {
  const fuzzyRegex = new RegExp(
    Array.from(search)
      .filter((char) => char.match(/[a-zA-Z0-9]/))
      .map((char) => `[${char}]`)
      .join('.*'),
    'i',
  );
  return label.match(fuzzyRegex);
};

const Picker = ({
  options = [],
  onPickerClick,
  onValueChange,
  render,
  searchTerm,
}: PickerProps) => {
  // Whether the picker is active (and shown to the user).
  const [pickerActive, setPickerActive] = useState<boolean>(false);
  // The index (relative to the `options` array) of the highlighted picker item, if it exists.
  const [pickerCursorOptionsIndex, setPickerCursorOptionsIndex] = useState<number | null>(null);

  // ------------------------------------------------------------------------------------------------
  // Compute the options that pass the search filter.
  // ------------------------------------------------------------------------------------------------

  // First, we compute the options that pass the search filter, and record their indices
  // (into the `options` array).
  const activeIndices: number[] = useMemo(
    () =>
      options.reduce((memo, option, index) => {
        if (searchMatch(option.label, searchTerm)) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          memo.push(index);
        }
        return memo;
      }, []),
    [options, searchTerm],
  );
  const activeOptions = activeIndices.map((index) => options[index]);

  // The index (relative to the `activeOptions` and `activeIndexes` arrays) of the highlighted
  // picker item, if it exists.
  const [pickerCursorActiveIndex, setPickerCursorActiveIndex] = useState<number | null>(null);

  const handleOutsideClick = () => {
    if (pickerActive) {
      setPickerActive(false);
      setPickerCursorActiveIndex(null);
      setPickerCursorOptionsIndex(null);
    }
  };

  const pickerDown = () => {
    let nextPickerCursorActiveIndex = 0;
    if (pickerCursorActiveIndex != null) {
      nextPickerCursorActiveIndex = (pickerCursorActiveIndex + 1) % activeIndices.length;
    }

    if (nextPickerCursorActiveIndex !== pickerCursorActiveIndex) {
      const cursorOptionIndex = activeIndices[nextPickerCursorActiveIndex];
      setPickerCursorActiveIndex(nextPickerCursorActiveIndex);
      setPickerCursorOptionsIndex(cursorOptionIndex);
      // Notify that the hovered item changed.
      onValueChange(options[cursorOptionIndex]);
    }
  };

  const pickerUp = () => {
    let nextPickerCursorActiveIndex = 0;
    if (pickerCursorActiveIndex != null) {
      nextPickerCursorActiveIndex =
        (pickerCursorActiveIndex + activeIndices.length - 1) % activeIndices.length;
    }
    setPickerCursorActiveIndex(nextPickerCursorActiveIndex);

    if (nextPickerCursorActiveIndex !== pickerCursorActiveIndex) {
      const cursorOptionIndex = activeIndices[nextPickerCursorActiveIndex];
      setPickerCursorActiveIndex(nextPickerCursorActiveIndex);
      setPickerCursorOptionsIndex(cursorOptionIndex);
      // Notify that the hovered item changed.
      onValueChange(options[cursorOptionIndex]);
    }
  };

  // Whenever the user updates the search term, we should readjust the highlighted option accordingly.
  useEffect(() => {
    const oldCursorOptionIndex = pickerCursorOptionsIndex;

    if (oldCursorOptionIndex == null) {
      // Nothing to update, if there was nothing previously highlighted.
      return;
    }

    if (searchMatch(options[oldCursorOptionIndex].label, searchTerm)) {
      // The old selected value is still valid, even after the user updated the search term.
      // The number of active options may have changed, so we'll readjust the index accordingly.
      const newCursorActiveIndex = activeIndices.find((index) => index === oldCursorOptionIndex);
      if (newCursorActiveIndex !== pickerCursorActiveIndex) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setPickerCursorActiveIndex(newCursorActiveIndex);
      }
    } else {
      // The old selected value is no longer valid.
      if (activeIndices.length > 0) {
        // We'll take the first valid option, if it exists.
        setPickerCursorActiveIndex(0);
        const cursorOptionIndex = activeIndices[0];
        setPickerCursorOptionsIndex(cursorOptionIndex);
        // Notify that the hovered item changed.
        onValueChange(options[cursorOptionIndex]);
      } else {
        // There are no more valid values.
        setPickerCursorActiveIndex(null);
        setPickerCursorOptionsIndex(null);
        onValueChange(null);

        // TODO: Figure out how we want to render this case.
      }
    }
  }, [
    activeIndices,
    onValueChange,
    options,
    pickerCursorActiveIndex,
    pickerCursorOptionsIndex,
    searchTerm,
  ]);

  // If we have some option highlighted, we'll scroll the picker so that it's visible.
  useEffect(() => {
    const el = document.getElementById(`pilled-input-picker-item-${pickerCursorOptionsIndex}`);
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
    }
  }, [pickerCursorOptionsIndex]);

  const handleItemMouseMove = (activeIndex: number) => (_e: MouseEvent<HTMLLIElement>) => {
    setPickerCursorActiveIndex(activeIndex);
    const newPickerCursorOptionsIndex = activeIndices[activeIndex];
    setPickerCursorOptionsIndex(newPickerCursorOptionsIndex);
    onValueChange(options[newPickerCursorOptionsIndex]);
  };

  const handleItemClick = (activeIndex: number) => (e: MouseEvent<HTMLLIElement>) => {
    // Use the activeIndex, not the pickerCursorOptionsIndex, because a click
    // event can occur over an item that hasn't had a move or hover
    // event previously.
    // e.g. touch input or scrolling without mouse movement
    const optionsIndex = activeIndices[activeIndex];
    onValueChange(options[optionsIndex]);
    onPickerClick(options[optionsIndex], e);
  };

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick} display='inline-block'>
      {render({ setPickerActive, pickerDown, pickerUp })}
      {pickerActive && options.length > 0 && (
        <PickerPositioner>
          <PickerContainer>
            {activeOptions.length > 0 ? (
              <PickerMenu>
                {activeOptions.map((option, activeIndex) => (
                  <PickerItem
                    id={`pilled-input-picker-item-${activeIndices[activeIndex]}`}
                    selected={options.indexOf(option) === pickerCursorOptionsIndex}
                    key={option.value}
                    value={option.value}
                    onMouseMove={handleItemMouseMove(activeIndex)}
                    onClick={handleItemClick(activeIndex)}
                    hide={!searchMatch(option.label, searchTerm)}
                  >
                    {option.label}
                  </PickerItem>
                ))}
              </PickerMenu>
            ) : (
              <NoOptionsText>No options</NoOptionsText>
            )}
          </PickerContainer>
        </PickerPositioner>
      )}
    </OutsideClickHandler>
  );
};

export default Picker;
